import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { MinusIcon, PlusIcon, XIcon, RepeatIcon } from 'lucide-react';
import { useCartActions, useOverlayActions } from '../../contexts/StoreContext';
import { ProductImage } from '../../ui/ProductImage';
import { productMeta } from '../../utils/catalog';
import { isSoldOut, maxQty } from '../../core/rules/stock';
import { canSubscribe } from '../../core/rules/features';
import { useFeatures } from '../../hooks/useFeatures';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { euro } from '../../core/format/currency';
import { ease } from '../../core/format/motion';
import type { CartLine as Line } from '../../contexts/StoreContext';

/** 40px raakvlak: de mand wordt vooral op de telefoon gebruikt. */
const stepBtn = 'flex h-10 w-10 items-center justify-center text-muted transition-colors duration-150 ease-soft hover:text-ink';

/** Eén regel in de mand: product, aantal, verwijderen en (bij navulbaar) automatisch bijvullen. */
export function CartLine({
  line
}: {
  line: Line;
}) {
  const {
    setQty,
    remove,
    toggleSubscribe
  } = useCartActions();
  const {
    closeCart
  } = useOverlayActions();
  const reduce = useReducedMotion();
  const features = useFeatures();
  const {
    product,
    qty,
    subscribe
  } = line;
  const meta = productMeta(product);
  const soldOut = isSoldOut(product);
  return <motion.li layout={!reduce} initial={reduce ? false : {
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={reduce ? {
    opacity: 0
  } : {
    opacity: 0,
    x: 24,
    transition: {
      duration: 0.18,
      ease
    }
  }} transition={{
    duration: 0.24,
    ease
  }} className="py-5" data-id="element-6973" data-mp="3q0bchb48s653">
      <div className="flex gap-4" data-id="element-6974" data-mp="25in5rjol9oxn">
        {/* Een kuur heeft geen productpagina; hij staat bij de behandelingen. */}
        <Link to={product.kind === 'kuur' ? '/behandelingen' : `/webshop/${product.id}`} onClick={closeCart} className="shrink-0" data-id="element-6975" data-mp="20k3i9euck536">
          <ProductImage src={product.image} alt={shopLabels.productImage(product)} className="h-24 w-20 shrink-0 p-2" data-id="element-6976" data-mp="2q90tdn631j78" />
        </Link>
        <div className="flex flex-1 flex-col" data-id="element-6977" data-mp="2ubdq5m2uq6rv">
          <div className="flex items-start justify-between gap-3" data-id="element-6978" data-mp="2z84booxfduko">
            <div data-id="element-6979" data-mp="347h0vgbrrdqf">
              <p className="text-[13px] text-clay" data-id="element-6980" data-mp="3480925bmanr9">{product.brand}</p>
              <h3 className="mt-0.5 font-display text-[1.05rem] leading-snug text-ink" data-id="element-6981" data-mp="3454b4gzrrz2t">{product.name}</h3>
              {meta && <p className="mt-0.5 text-[13px] text-muted" data-id="element-6982" data-mp="3d7x0uftb02qj">{meta}</p>}
            </div>
            <button type="button" onClick={() => remove(product.id)} aria-label={shopLabels.remove(product)} className={`-mr-2 -mt-1 ${stepBtn}`} data-id="element-6983" data-mp="3hyvjvn5ofexh">
              <XIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-6984" data-mp="3mwna8cqoaszk" />
            </button>
          </div>

          {soldOut && <p className="mt-2 text-[13px] leading-snug text-clay" data-id="element-6985" data-mp="3rv8agc5xdco0">
              Inmiddels uitverkocht.{' '}
              <button type="button" onClick={() => remove(product.id)} className="text-ink underline underline-offset-4" data-id="element-6986" data-mp="1l8svommsit7u">
                Weghalen<span className="sr-only" data-id="element-6987" data-mp="1g9g63u4jb81k">: {product.name}</span>
              </button>{' '}
              of{' '}
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="text-ink underline underline-offset-4" data-id="element-6988" data-mp="1bbedahhb3ucw">
                app {founder.firstName}
                <span className="sr-only" data-id="element-6989" data-mp="23z0skmzwcnfl">{shopLabels.viaWhatsApp}</span>
              </a>{' '}
              voor een alternatief.
            </p>}

          <div className="mt-auto flex items-center justify-between pt-3" data-id="element-6990" data-mp="1z0z8byqqii2j">
            <div className="flex items-center border border-line" data-id="element-6991" data-mp="1u865jgettjt3">
              {/* Bij 1 haalt "min" het product weg: dan zegt de knop dat ook. */}
              <button type="button" aria-label={qty <= 1 ? shopLabels.remove(product) : shopLabels.fewer(product)}
            // Bij 1 via remove: dan is er ook "Ongedaan maken".
            onClick={() => qty <= 1 ? remove(product.id) : setQty(product.id, qty - 1)} className={stepBtn} data-id="element-6992" data-mp="2mprz7gsi1zqa">
                <MinusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6993" data-mp="27hpt77hr6dwc" />
              </button>
              <span className="min-w-[1.75rem] text-center text-[14px] text-ink" aria-live="polite" data-id="element-6994" data-mp="1skyvd2nq9a8u">
                <span aria-hidden="true" data-id="element-6995" data-mp="1xk1pvt62zy2k">{qty}</span>
                <span className="sr-only" data-id="element-6996" data-mp="2l79wcef8xd66">{shopLabels.quantity(product, qty)}</span>
              </span>
              <button type="button" aria-label={shopLabels.more(product)} onClick={() => setQty(product.id, qty + 1)} disabled={soldOut || qty >= maxQty(product)} className={`${stepBtn} disabled:cursor-not-allowed disabled:opacity-40`} data-id="element-6997" data-mp="2q5l50ltt1hmt">
                <PlusIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6998" data-mp="3452u72b0iwwp" />
              </button>
            </div>
            <span className="font-display text-lg text-ink" data-id="element-6999" data-mp="xi59tpjbsmvy">{euro(product.price * qty)}</span>
          </div>
          {/* Waarom "+" uit staat: anders lijkt de knop kapot. */}
          {!soldOut && qty >= maxQty(product) && <p className="mt-1.5 text-[13px] text-muted" data-id="element-7000" data-mp="178gddaz3ku2b">Meer hebben we nu niet op voorraad.</p>}
        </div>
      </div>

      {canSubscribe(features, product) && <label className="mt-3 flex cursor-pointer items-start gap-3 bg-sand p-3" data-id="element-7001" data-mp="12b7uf864km00">
          <input type="checkbox" checked={subscribe} onChange={() => toggleSubscribe(product.id)} className="mt-0.5 h-4 w-4 shrink-0 accent-clay" data-id="element-7002" data-mp="ejj8c9jwctri" />
          <span className="text-[13px] leading-snug text-muted" data-id="element-7003" data-mp="nkh464k6luz0">
            <span className="flex items-center gap-1.5 text-ink" data-id="element-7004" data-mp="nhmjcbx9a852">
              <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-7005" data-mp="jgsungbzopb4" />
              Automatisch bijvullen
            </span>
            Zelfde prijs, {policyCopy.subscriptionPerks}.
          </span>
        </label>}
    </motion.li>;
}